<template>
  <div class="shop-page">
    <!-- 1) Barra de anuncio -->
    <div class="announcement">Envíos a toda Colombia · Mínimo 6 unidades por pedido</div>

    <!-- 2) Header sticky -->
    <header class="header" :class="{ 'header--scrolled': scrolled }">
      <div class="container-x">
        <div class="header__inner">
          <div class="header__brand">
            <img
              v-if="logoSrc"
              :src="logoSrc"
              alt="Logo"
              class="header__logo"
              @error="logoFailed = true"
            />
            <img v-else :src="logoFallback" alt="Logo" class="header__logo" />
            <span class="header__name">Belezza</span>
          </div>

          <label class="header__search">
            <i class="fas fa-search header__search-icon"></i>
            <input
              v-model="search"
              type="text"
              class="header__search-input"
              placeholder="Buscar"
            />
            <button
              v-if="search"
              class="header__search-clear"
              aria-label="Limpiar búsqueda"
              @click="search = ''"
            >
              <i class="fas fa-times"></i>
            </button>
          </label>

          <button class="header__cart" aria-label="Ver carrito" @click="openCart">
            <i class="fas fa-shopping-bag"></i>
            <span v-if="cartCount > 0" class="header__cart-bubble">{{ cartBubble }}</span>
          </button>
        </div>

        <div class="header__row2">
          <label class="header__search header__search--mobile">
            <i class="fas fa-search header__search-icon"></i>
            <input
              v-model="search"
              type="text"
              class="header__search-input"
              placeholder="Buscar"
            />
            <button
              v-if="search"
              class="header__search-clear"
              aria-label="Limpiar búsqueda"
              @click="search = ''"
            >
              <i class="fas fa-times"></i>
            </button>
          </label>
        </div>
      </div>
    </header>

    <!-- 3) Colección -->
    <main class="collection">
      <div class="container-x">
        <div class="collection__head">
          <h1>Catálogo Mayorista</h1>
          <p v-if="vendedorNombre">
            Atendido por <strong>{{ vendedorNombre }}</strong> · Pedidos por WhatsApp
          </p>
        </div>

        <!-- 4) Toolbar -->
        <div class="toolbar">
          <span class="toolbar__count">{{ productosFiltrados.length }} productos</span>
          <label class="toolbar__sort">
            <span class="toolbar__label">Ordenar por</span>
            <span class="select-wrap">
              <select v-model="sortBy">
                <option value="destacados">Destacados</option>
                <option value="az">Alfabéticamente A–Z</option>
                <option value="za">Z–A</option>
                <option value="precio_asc">Precio menor a mayor</option>
                <option value="precio_desc">Precio mayor a menor</option>
                <option value="stock">Mayor stock</option>
              </select>
              <i class="fas fa-chevron-down"></i>
            </span>
          </label>
        </div>

        <!-- 5) Chips de categorías -->
        <div v-if="categorias.length" class="cat-chips">
          <button
            class="cat-chip"
            :class="{ 'cat-chip--active': activeCat === null }"
            @click="activeCat = null"
          >
            Todas
          </button>
          <button
            v-for="cat in categorias"
            :key="cat.id"
            class="cat-chip"
            :class="{ 'cat-chip--active': activeCat === cat.id }"
            @click="activeCat = cat.id"
          >
            {{ cat.label }}
          </button>
        </div>

        <!-- 7) Estado de carga -->
        <div v-if="loading" class="pgrid">
          <div v-for="n in 8" :key="'sk-' + n" class="pcard skeleton">
            <div class="skeleton__media"></div>
            <div class="skeleton__line" style="width: 40%"></div>
            <div class="skeleton__line" style="width: 80%"></div>
            <div class="skeleton__line" style="width: 25%"></div>
          </div>
        </div>

        <!-- 7) Estado de error -->
        <div v-else-if="error" class="shop-state">
          <i class="fas fa-exclamation-circle shop-state__icon"></i>
          <p>Ocurrió un error al cargar el catálogo.</p>
          <button class="shop-state__link" @click="cargarCatalogo">Reintentar</button>
        </div>

        <!-- 7) Estado vacío -->
        <div v-else-if="productosFiltrados.length === 0" class="shop-state">
          <i class="fas fa-search shop-state__icon"></i>
          <p>No se encontraron productos</p>
          <button class="shop-state__link" @click="limpiarFiltros">Limpiar búsqueda</button>
        </div>

        <!-- 6) Grilla de productos -->
        <div v-else class="pgrid">
          <article v-for="p in productosFiltrados" :key="p.matid" class="pcard">
            <div class="pcard__media">
              <img
                v-if="imagenDe(p) && !p._imgError"
                :src="imagenDe(p)"
                :alt="p.descrip"
                loading="lazy"
                @error="p._imgError = true"
              />
              <i v-else class="fas fa-tshirt pcard__placeholder"></i>

              <span v-if="esAgotado(p)" class="badge badge--soldout">Agotado</span>
              <span v-else class="badge badge--stock">Stock: {{ stockLabel(p.existencia) }}</span>

              <button
                class="pcard__quick"
                :disabled="esAgotado(p)"
                @click="agregarRapido(p)"
              >
                <i class="fas" :class="esAgotado(p) ? 'fa-ban' : 'fa-plus'"></i>
                {{ esAgotado(p) ? 'Agotado' : 'Agregar' }}
              </button>
            </div>

            <div class="pcard__info">
              <h3 class="pcard__title">{{ p.descrip }}</h3>
              <div class="pcard__price-row">
                <span class="pcard__price">{{ formatoCOP(p.precio) }}</span>
                <span v-if="trm > 0" class="tag tag--cop">COP</span>
                <span v-if="trm > 0" class="tag tag--usd">USD {{ formatoUSD(p.precio) }}</span>
              </div>
              <p class="pcard__ref">Ref. {{ p.codigo }}</p>
            </div>
          </article>
        </div>
      </div>
    </main>

    <!-- 8) Footer -->
    <footer class="shop-footer">
      <img v-if="logoSrc && !logoFailed" :src="logoSrc" alt="Logo" class="shop-footer__logo" @error="logoFailed = true" />
      <img v-else :src="logoFallback" alt="Logo" class="shop-footer__logo" />
      <p class="shop-footer__name">Belezza</p>
      <p class="shop-footer__tagline">Catálogo mayorista · Mínimo 6 unidades por pedido</p>
      <p class="shop-footer__copy">© {{ anio }} Belezza. Todos los derechos reservados.</p>
    </footer>

    <!-- 9) Drawer del carrito -->
    <transition name="ofade">
      <div v-if="cartOpen" class="cart-overlay" @click="closeCart"></div>
    </transition>
    <aside class="cart" :class="{ 'cart--open': cartOpen }" aria-label="Carrito">
      <div class="cart__head">
        <h2 class="cart__title">Tu carrito ({{ cartCount }})</h2>
        <button class="icon-btn" aria-label="Cerrar carrito" @click="closeCart">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div v-if="cart.length === 0" class="cart__empty">
        <i class="fas fa-shopping-bag cart__empty-icon"></i>
        <p>Tu carrito está vacío</p>
        <button class="btn btn--dark btn--block" @click="closeCart">Continuar comprando</button>
      </div>

      <div v-else class="cart__items">
        <div v-for="item in cart" :key="item.key" class="cart__item">
          <div class="cart__thumb">
            <img
              v-if="imagenDe(item.producto) && !item.producto._imgError"
              :src="imagenDe(item.producto)"
              :alt="item.nombre"
            />
            <i v-else class="fas fa-tshirt"></i>
          </div>
          <div class="cart__item-info">
            <p class="cart__item-name">{{ item.nombre }}</p>
            <span v-if="item.detalle" class="cart__item-variant">{{ item.detalle }}</span>
            <p class="cart__item-price">{{ formatoCOP(item.precio) }} c/u</p>
            <div class="cart__item-row">
              <span class="stepper">
                <button aria-label="Restar" @click="cambiarCantidad(item, -1)">
                  <i class="fas fa-minus"></i>
                </button>
                <span class="stepper__num">{{ item.qty }}</span>
                <button aria-label="Sumar" @click="cambiarCantidad(item, 1)">
                  <i class="fas fa-plus"></i>
                </button>
              </span>
              <button class="cart__item-remove" @click="eliminarLinea(item)">Eliminar</button>
            </div>
          </div>
          <span class="cart__item-total">{{ formatoCOP(item.precio * item.qty) }}</span>
        </div>
      </div>

      <div v-if="cart.length > 0" class="cart__foot">
        <div class="cart__minbar">
          <div class="cart__minbar-rail">
            <div class="cart__minbar-fill" :style="{ width: minProgress * 100 + '%' }"></div>
          </div>
          <p class="cart__minbar-text" :class="{ 'cart__minbar-text--ok': cartCount >= MINIMO }">
            {{ cartCount }}/{{ MINIMO }} unidades · mínimo por pedido
          </p>
        </div>

        <div class="cart__subtotal">
          <span class="cart__subtotal-label">Subtotal</span>
          <span class="cart__subtotal-value">
            {{ formatoCOP(cartTotal) }}
            <span v-if="trm > 0" class="tag tag--cop">COP</span>
            <span v-if="trm > 0" class="tag tag--usd">USD {{ formatoUSD(cartTotal) }}</span>
          </span>
        </div>

        <p class="cart__note">El pedido se envía y confirma por WhatsApp.</p>

        <form class="cart__form" @submit.prevent="enviarPedido">
          <input
            v-model="form.nombre"
            type="text"
            class="field"
            placeholder="Nombre completo *"
          />
          <div class="cart__form-row">
            <select v-model="form.departamento" class="field">
              <option value="" disabled>Departamento *</option>
              <option v-for="d in dptos" :key="d" :value="d">{{ d }}</option>
            </select>
            <select v-model="form.ciudad" class="field" :disabled="!form.departamento">
              <option value="" disabled>Ciudad *</option>
              <option v-for="c in ciudadesFiltradas" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <input
            v-model="form.direccion"
            type="text"
            class="field"
            placeholder="Dirección *"
          />

          <button
            type="submit"
            class="btn btn--dark btn--block"
            :disabled="!formCompleta || sending"
          >
            <i v-if="sending" class="fas fa-spinner fa-spin"></i>
            <template v-else><i class="fab fa-whatsapp"></i> Enviar pedido por WhatsApp</template>
          </button>
          <p v-if="orderError" class="cart__error">{{ orderError }}</p>
        </form>
      </div>
    </aside>

    <!-- 10) Modal de variantes -->
    <transition name="vfade">
      <div v-if="modal.open" class="vmodal-overlay" @click.self="closeModal">
        <div class="vmodal" role="dialog" aria-modal="true">
          <div class="vmodal__head">
            <h3 class="vmodal__title">{{ modal.producto ? modal.producto.descrip : '' }}</h3>
            <button class="icon-btn" aria-label="Cerrar" @click="closeModal">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="vmodal__body">
            <div v-if="modal.loading" class="vmodal__loading">
              <i class="fas fa-spinner fa-spin"></i>
            </div>
            <template v-else>
              <p class="vmodal__hint">Selecciona las cantidades por talla y color…</p>
              <div
                v-for="s in modal.serials"
                :key="s.serialid"
                class="vmodal__item"
                :class="{ 'vmodal__item--disabled': s.saldo <= 0 }"
              >
                <span class="vmodal__name">{{ nombreVariante(s) }}</span>
                <span
                  class="color-dot"
                  :style="{ background: s.colormin || '#e8e8ec' }"
                ></span>
                <span
                  class="vmodal__disp"
                  :class="s.saldo > 0 ? 'vmodal__disp--ok' : 'vmodal__disp--out'"
                >
                  Disp.: {{ s.saldo }}
                </span>
                <span class="stepper stepper--modal">
                  <button
                    aria-label="Restar"
                    :disabled="(qtyDe(s.serialid) || 0) <= 0"
                    @click="fijarCantidad(s, qtyDe(s.serialid) - 1)"
                  >
                    <i class="fas fa-minus"></i>
                  </button>
                  <input
                    class="stepper__input"
                    type="text"
                    inputmode="numeric"
                    :value="qtyDe(s.serialid) || 0"
                    @input="fijarCantidad(s, $event.target.value)"
                  />
                  <button
                    aria-label="Sumar"
                    :disabled="(qtyDe(s.serialid) || 0) >= s.saldo"
                    @click="fijarCantidad(s, qtyDe(s.serialid) + 1)"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                </span>
              </div>
            </template>
          </div>

          <div class="vmodal__foot">
            <span class="vmodal__total">Total: <strong>{{ modalTotal }}</strong></span>
            <button
              class="btn btn--dark"
              :disabled="modalTotal <= 0"
              @click="confirmarVariantes"
            >
              <i class="fas fa-cart-plus"></i> Agregar al carrito
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import catalogService from '@/services/catalog.service';
import logoFallback from '@/assets/img/logo-sm.png';

// Mínimo de unidades totales por pedido
const MIN_TOTAL_UNIDADES = 6;

export default {
  name: 'PublicCatalog',
  data() {
    return {
      MINIMO: MIN_TOTAL_UNIDADES,
      anio: new Date().getFullYear(),
      token: '',
      logoFallback,
      logoSrc: '',
      logoFailed: false,
      vendedorNombre: '',
      trm: 0,
      productos: [],
      loading: true,
      error: false,
      search: '',
      sortBy: 'destacados',
      activeCat: null,
      scrolled: false,
      // Carrito (agrupa por matid + nroserial)
      cart: [],
      cartOpen: false,
      // Modal de variantes
      modal: { open: false, loading: false, producto: null, serials: [], qty: {} },
      // Checkout
      form: { nombre: '', departamento: '', ciudad: '', direccion: '' },
      ciudadesData: [],
      sending: false,
      orderError: '',
    };
  },
  computed: {
    cartCount() {
      return this.cart.reduce((s, i) => s + i.qty, 0);
    },
    cartTotal() {
      return this.cart.reduce((s, i) => s + i.precio * i.qty, 0);
    },
    cartBubble() {
      return this.cartCount > 99 ? '99+' : String(this.cartCount);
    },
    minProgress() {
      return Math.min(this.cartCount / MIN_TOTAL_UNIDADES, 1);
    },
    formCompleta() {
      const f = this.form;
      return !!(f.nombre && f.departamento && f.ciudad && f.direccion) && !this.sending;
    },
    dptos() {
      return [...new Set(this.ciudadesData.map((c) => c.DPTO))].sort((a, b) =>
        a.localeCompare(b, 'es')
      );
    },
    ciudadesFiltradas() {
      return this.ciudadesData
        .filter((c) => c.DPTO === this.form.departamento)
        .map((c) => c.NOMBRE)
        .sort((a, b) => a.localeCompare(b, 'es'));
    },
    categorias() {
      // Chips jerárquicos: el padre agrupa sus subcategorías
      const mapa = new Map();
      for (const p of this.productos) {
        if (p.categoria_id != null && p.categoria_padre_id != null) {
          if (!mapa.has(p.categoria_padre_id)) {
            mapa.set(p.categoria_padre_id, {
              id: p.categoria_padre_id,
              label: p.categoria_padre_nombre,
              esPadre: true,
            });
          }
          if (!mapa.has(p.categoria_id)) {
            mapa.set(p.categoria_id, {
              id: p.categoria_id,
              label: `${p.categoria_padre_nombre} > ${p.categoria_nombre}`,
              esPadre: false,
            });
          }
        } else if (p.categoria_id != null && !mapa.has(p.categoria_id)) {
          mapa.set(p.categoria_id, { id: p.categoria_id, label: p.categoria_nombre, esPadre: false });
        }
      }
      return [...mapa.values()];
    },
    productosFiltrados() {
      const q = this.search.trim().toLowerCase();
      let lista = this.productos.filter((p) => {
        if (q) {
          const campos = `${p.descrip} ${p.codigo} ${p.marca_descrip}`.toLowerCase();
          if (!campos.includes(q)) return false;
        }
        if (this.activeCat != null) {
          const cat = this.categorias.find((c) => c.id === this.activeCat);
          if (!cat) return false;
          if (cat.esPadre) {
            if (p.categoria_id !== this.activeCat && p.categoria_padre_id !== this.activeCat) return false;
          } else if (p.categoria_id !== this.activeCat) {
            return false;
          }
        }
        return true;
      });

      const por = this.sortBy;
      if (por === 'az') lista = [...lista].sort((a, b) => a.descrip.localeCompare(b.descrip, 'es'));
      else if (por === 'za') lista = [...lista].sort((a, b) => b.descrip.localeCompare(a.descrip, 'es'));
      else if (por === 'precio_asc') lista = [...lista].sort((a, b) => a.precio - b.precio);
      else if (por === 'precio_desc') lista = [...lista].sort((a, b) => b.precio - a.precio);
      else if (por === 'stock') lista = [...lista].sort((a, b) => b.existencia - a.existencia);
      return lista;
    },
    modalTotal() {
      return Object.values(this.modal.qty).reduce((s, n) => s + (parseInt(n, 10) || 0), 0);
    },
  },
  watch: {
    // La ciudad se deshabilita hasta elegir departamento
    'form.departamento'() {
      this.form.ciudad = '';
    },
    cartOpen() {
      this.bloquearScroll();
    },
    'modal.open'() {
      this.bloquearScroll();
    },
  },
  mounted() {
    this.token = this.$route.params.token;
    document.title = 'Catálogo Mayorista — Belezza';
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('keydown', this.onKeydown);
    this.cargarCatalogo();
    this.cargarAjustes();
    this.cargarCiudades();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  },
  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 1;
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.modal.open) this.closeModal();
        else if (this.cartOpen) this.closeCart();
      }
    },
    bloquearScroll() {
      const bloqueado = this.cartOpen || this.modal.open;
      document.body.style.overflow = bloqueado ? 'hidden' : '';
    },
    async cargarCatalogo() {
      this.loading = true;
      this.error = false;
      try {
        const res = await catalogService.getCatalogo(this.token);
        this.vendedorNombre = res.data.vendedor?.nombre || '';
        this.productos = (res.data.productos || []).map((p) => ({ ...p, _imgError: false }));
      } catch (e) {
        console.error('Error cargando catálogo:', e);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async cargarAjustes() {
      try {
        const res = await catalogService.getPublicSettings();
        const s = res.data || {};
        this.trm = parseFloat(s.trm_dolar) || 0;
        const ruta = s.logo_url || s.logo_path || '';
        this.logoSrc = this.resolveImagen(ruta);
      } catch (e) {
        console.error('Error cargando ajustes públicos:', e);
      }
    },
    async cargarCiudades() {
      try {
        const res = await fetch(`${process.env.BASE_URL}ciudades.json?v=${Date.now()}`);
        this.ciudadesData = await res.json();
      } catch (e) {
        console.error('Error cargando ciudades:', e);
      }
    },
    // Las imágenes se resuelven contra VUE_APP_API_IMAGE_URL + /storage/{path}
    resolveImagen(ruta) {
      if (!ruta) return '';
      if (ruta.startsWith('http')) return ruta;
      const base = (process.env.VUE_APP_API_IMAGE_URL || '').trim().replace(/\/?$/, '/');
      return base + String(ruta).replace(/^\/?storage\//, '');
    },
    imagenDe(p) {
      return p ? this.resolveImagen(p.imagen) : '';
    },
    stockLabel(n) {
      return n >= 1000 ? '999+' : String(n);
    },
    esAgotado(p) {
      return (parseFloat(p.existencia) || 0) <= 0;
    },
    formatoCOP(v) {
      return '$' + Math.round(parseFloat(v) || 0).toLocaleString('es-CO');
    },
    formatoUSD(v) {
      return '$' + ((parseFloat(v) || 0) / this.trm).toFixed(2);
    },
    limpiarFiltros() {
      this.search = '';
      this.activeCat = null;
    },
    abrirModal(producto) {
      this.modal = { open: true, loading: true, producto, serials: [], qty: {} };
      catalogService
        .getSerials(this.token, producto.matid)
        .then((res) => {
          // Si al final no hay variantes, se agrega directo (como Shopify)
          if (!res.data || res.data.length === 0) {
            this.modal = { open: false, loading: false, producto: null, serials: [], qty: {} };
            this.agregarDirecto(producto, null, 1);
            this.openCart();
            return;
          }
          this.modal.serials = res.data;
          this.modal.loading = false;
        })
        .catch((e) => {
          console.error('Error cargando serials:', e);
          this.modal = { open: false, loading: false, producto: null, serials: [], qty: {} };
        });
    },
    closeModal() {
      this.modal.open = false;
    },
    nombreVariante(s) {
      const talla = (s.destalla || '').trim();
      const color = (s.descolor || '').trim();
      if (talla && color) return `${talla} / ${color}`;
      return talla || color || 'Variante';
    },
    qtyDe(serialid) {
      return this.modal.qty[serialid] || 0;
    },
    fijarCantidad(serial, valor) {
      let n = parseInt(valor, 10);
      if (isNaN(n) || n < 0) n = 0;
      if (n > serial.saldo) n = serial.saldo;
      // Reactivo: reasignar el objeto para que Vue detecte claves nuevas
      this.modal.qty = { ...this.modal.qty, [serial.serialid]: n };
    },
    confirmarVariantes() {
      const producto = this.modal.producto;
      let total = 0;
      for (const s of this.modal.serials) {
        const qty = this.qtyDe(s.serialid);
        if (qty > 0) {
          this.agregarDirecto(producto, s, qty);
          total += qty;
        }
      }
      this.modal = { open: false, loading: false, producto: null, serials: [], qty: {} };
      if (total > 0) this.openCart();
    },
    agregarRapido(producto) {
      if (producto.existencia <= 0) return;
      if (producto.variantes_count > 0) {
        this.abrirModal(producto);
        return;
      }
      this.agregarDirecto(producto, null, 1);
      this.openCart();
    },
    // Inserta o acumula una línea agrupada por matid + nroserial
    agregarDirecto(producto, serial, qty) {
      const nroserial = serial ? String(serial.serialid) : '0';
      const key = `${producto.matid}_${nroserial}`;
      const existente = this.cart.find((i) => i.key === key);
      if (existente) {
        existente.qty += qty;
      } else {
        this.cart.push({
          key,
          matid: producto.matid,
          serialid: serial ? serial.serialid : null,
          nroserial,
          nombre: producto.descrip,
          detalle: serial
            ? [serial.destalla, serial.descolor].filter(Boolean).join(' / ')
            : '',
          precio: parseFloat(producto.precio) || 0,
          qty,
          producto,
        });
      }
    },
    openCart() {
      this.cartOpen = true;
    },
    closeCart() {
      this.cartOpen = false;
    },
    cambiarCantidad(item, delta) {
      const nueva = item.qty + delta;
      if (nueva <= 0) {
        this.eliminarLinea(item);
        return;
      }
      item.qty = nueva;
    },
    eliminarLinea(item) {
      this.cart = this.cart.filter((i) => i.key !== item.key);
    },
    async enviarPedido() {
      this.orderError = '';
      if (this.cartCount < MIN_TOTAL_UNIDADES) {
        this.orderError = `El pedido mínimo es de ${MIN_TOTAL_UNIDADES} unidades (llevas ${this.cartCount}).`;
        return;
      }
      if (!this.formCompleta) return;
      this.sending = true;
      try {
        const res = await catalogService.sendPedido(this.token, {
          items: this.cart.map((i) => ({ matid: i.matid, serialid: i.serialid, qty: i.qty })),
          cliente: {
            nombre: this.form.nombre,
            departamento: this.form.departamento,
            ciudad: this.form.ciudad,
            direccion: this.form.direccion,
          },
        });
        window.open(res.data.whatsapp_url, '_blank');
      } catch (e) {
        this.orderError =
          e.response?.data?.message || 'No se pudo enviar el pedido. Intenta de nuevo.';
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Assistant:wght@400;500;600;700&display=swap");
</style>

<style scoped>
/* ===== Tokens de diseño ===== */
.shop-page {
  --ink: #121212;
  --ink-75: rgba(18, 18, 18, 0.75);
  --ink-55: rgba(18, 18, 18, 0.55);
  --line: rgba(18, 18, 18, 0.1);
  --brand: #ec91ba;
  --brand-dark: #d478a8;

  background: #fff;
  color: var(--ink);
  font-family: "Assistant", "Segoe UI", system-ui, sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.container-x {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  width: 100%;
}
@media (min-width: 768px) {
  .container-x {
    padding: 0 32px;
  }
}

/* ===== 1) Barra de anuncio ===== */
.announcement {
  background: var(--ink);
  color: #fff;
  text-align: center;
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* ===== 2) Header sticky ===== */
.header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  border-bottom: 1px solid var(--line);
  transition: box-shadow 0.2s;
}
.header--scrolled {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}
.header__inner {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 64px;
}
.header__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.header__logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--line);
  padding: 2px;
  object-fit: cover;
  background: #fff;
}
.header__name {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.header__search {
  position: relative;
  display: block;
  flex: 1;
  max-width: 340px;
  margin-left: auto;
}
.header__search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: var(--ink-55);
  pointer-events: none;
}
.header__search-input {
  width: 100%;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 999px;
  padding: 9px 34px 9px 36px;
  font-size: 13.5px;
  font-family: inherit;
  color: var(--ink);
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}
.header__search-input:focus {
  border-color: var(--ink);
}
.header__search-input::placeholder {
  color: rgba(18, 18, 18, 0.45);
}
.header__search-clear {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-55);
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.header__search-clear:hover {
  background: rgba(18, 18, 18, 0.06);
  color: var(--ink);
}
.header__cart {
  position: relative;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink);
  font-size: 19px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.header__cart:hover {
  background: rgba(18, 18, 18, 0.05);
}
.header__cart-bubble {
  position: absolute;
  top: 2px;
  right: 1px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--brand);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fff;
}
.header__row2 {
  display: none;
}

/* ===== 3) Título de colección ===== */
.collection {
  padding-top: 36px;
  flex: 1;
}
.collection__head {
  text-align: center;
  margin-bottom: 24px;
}
.collection__head h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: var(--ink);
}
.collection__head p {
  margin: 6px 0 0;
  font-size: 13.5px;
  color: var(--ink-55);
}
.collection__head p strong {
  font-weight: 600;
  color: var(--ink);
}

/* ===== 4) Toolbar ===== */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}
.toolbar__count {
  font-size: 13px;
  color: var(--ink-55);
}
.toolbar__sort {
  display: flex;
  align-items: center;
  gap: 8px;
}
.toolbar__label {
  font-size: 12.5px;
  color: var(--ink-55);
}
.select-wrap {
  position: relative;
  display: inline-flex;
}
.select-wrap select {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 8px;
  background: #fff;
  padding: 8px 30px 8px 12px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  color: var(--ink);
  cursor: pointer;
  outline: none;
}
.select-wrap i {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  color: var(--ink-55);
  pointer-events: none;
}

/* ===== 5) Chips de categorías ===== */
.cat-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}
.cat-chip {
  border: 1px solid rgba(18, 18, 18, 0.2);
  background: #fff;
  color: var(--ink);
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.cat-chip:hover {
  border-color: var(--ink);
}
.cat-chip--active {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff;
}

/* ===== 6) Grilla y tarjeta de producto ===== */
.pgrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px 12px;
}
.pcard {
  display: flex;
  flex-direction: column;
}
.pcard__media {
  position: relative;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  overflow: hidden;
  background: #f6f6f6;
  transition: box-shadow 0.25s;
}
.pcard:hover .pcard__media {
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.08);
}
.pcard__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s;
}
.pcard:hover .pcard__media img {
  transform: scale(1.04);
}
.pcard__placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 38px;
  color: rgba(18, 18, 18, 0.15);
}
.badge {
  position: absolute;
  top: 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 5px 10px;
  z-index: 1;
}
.badge--soldout {
  left: 10px;
  background: #fff;
  color: var(--ink);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.badge--stock {
  right: 10px;
  background: rgba(18, 18, 18, 0.72);
  color: #fff;
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
}
.pcard__quick {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  background: var(--ink);
  color: #fff;
  padding: 11px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-family: inherit;
  cursor: pointer;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.2s, transform 0.2s, background 0.15s, color 0.15s;
}
.pcard:hover .pcard__quick,
.pcard__quick:focus-visible {
  opacity: 1;
  transform: translateY(0);
}
.pcard__quick:hover:not(:disabled) {
  background: #000;
}
.pcard__quick:disabled {
  background: rgba(18, 18, 18, 0.08);
  color: rgba(18, 18, 18, 0.45);
  cursor: not-allowed;
  opacity: 0;
  transform: translateY(6px);
}
.pcard:hover .pcard__quick:disabled {
  opacity: 1;
  transform: translateY(0);
}
@media (hover: none) {
  .pcard__quick,
  .pcard__quick:disabled {
    opacity: 1;
    transform: none;
  }
}
.pcard__info {
  padding: 12px 2px 0;
  text-align: left;
}
.pcard__title {
  margin: 0;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.45;
  color: var(--ink);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.pcard__price-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}
.pcard__price {
  font-size: 14.5px;
  font-weight: 700;
}
.tag {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
}
.tag--cop {
  background: #eeeef4;
  color: #5c5c72;
  border-color: #d6d6e0;
}
.tag--usd {
  background: #e8f5ec;
  color: #1a7a3c;
  border-color: #bfe3cb;
}
.pcard__ref {
  margin: 2px 0 0;
  font-size: 11.5px;
  color: var(--ink-55);
}

/* ===== 7) Estados ===== */
.skeleton {
  pointer-events: none;
}
.skeleton__media {
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  background: linear-gradient(100deg, #f0f0f0 40%, #f7f7f7 50%, #f0f0f0 60%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite linear;
}
.skeleton__line {
  height: 11px;
  border-radius: 6px;
  margin-top: 12px;
  background: linear-gradient(100deg, #f0f0f0 40%, #f7f7f7 50%, #f0f0f0 60%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite linear;
}
@keyframes shimmer {
  0% {
    background-position: 120% 0;
  }
  100% {
    background-position: -80% 0;
  }
}
.shop-state {
  text-align: center;
  padding: 72px 20px;
  color: var(--ink-55);
}
.shop-state__icon {
  font-size: 34px;
  opacity: 0.5;
}
.shop-state p {
  margin: 12px 0 0;
  font-size: 13.5px;
  color: var(--ink-75);
}
.shop-state__link {
  border: none;
  background: none;
  padding: 0;
  margin-top: 8px;
  font-size: 13px;
  font-family: inherit;
  color: var(--ink);
  text-decoration: underline;
  cursor: pointer;
}
.shop-state__link:hover {
  color: var(--brand-dark);
}

/* ===== 8) Footer ===== */
.shop-footer {
  border-top: 1px solid var(--line);
  margin-top: 48px;
  padding: 40px 20px 48px;
  text-align: center;
}
.shop-footer__logo {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid var(--line);
  padding: 2px;
  object-fit: cover;
}
.shop-footer__name {
  margin: 12px 0 0;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.shop-footer__tagline {
  margin: 6px 0 0;
  font-size: 12.5px;
  color: var(--ink-55);
}
.shop-footer__copy {
  margin: 14px 0 0;
  font-size: 11.5px;
  color: rgba(18, 18, 18, 0.4);
}

/* ===== 9) Drawer del carrito ===== */
.cart-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 18, 18, 0.45);
  z-index: 200;
}
.ofade-enter-active,
.ofade-leave-active {
  transition: opacity 0.2s;
}
.ofade-enter-from,
.ofade-leave-to {
  opacity: 0;
}
.cart {
  position: fixed;
  right: 0;
  top: 0;
  width: 100%;
  max-width: 430px;
  height: 100vh;
  height: 100dvh;
  background: #fff;
  z-index: 201;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.14);
  transform: translateX(100%);
  transition: transform 0.28s;
}
.cart--open {
  transform: translateX(0);
}
.cart__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  flex-shrink: 0;
}
.cart__title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
}
.icon-btn {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink);
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.icon-btn:hover {
  background: rgba(18, 18, 18, 0.06);
}
.cart__empty {
  text-align: center;
  padding: 48px 20px;
}
.cart__empty-icon {
  font-size: 34px;
  opacity: 0.4;
}
.cart__empty p {
  margin: 12px 0 16px;
  font-size: 13.5px;
  color: var(--ink-55);
}
.cart__items {
  flex: 1;
  overflow-y: auto;
  padding: 4px 20px;
}
.cart__item {
  display: flex;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(18, 18, 18, 0.06);
}
.cart__item:last-child {
  border-bottom: none;
}
.cart__thumb {
  width: 68px;
  height: 88px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #f6f6f6;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(18, 18, 18, 0.15);
  font-size: 22px;
}
.cart__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cart__item-info {
  flex: 1;
  min-width: 0;
}
.cart__item-name {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
}
.cart__item-variant {
  display: inline-flex;
  margin-top: 4px;
  background: #f4f4f4;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 10.5px;
  font-weight: 600;
  color: var(--ink-75);
}
.cart__item-price {
  margin: 4px 0 0;
  font-size: 11.5px;
  color: var(--ink-55);
}
.cart__item-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}
.cart__item-remove {
  border: none;
  background: none;
  padding: 0;
  font-size: 11.5px;
  font-family: inherit;
  color: var(--ink-55);
  text-decoration: underline;
  cursor: pointer;
}
.cart__item-remove:hover {
  color: #b3261e;
}
.cart__item-total {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}
.cart__foot {
  border-top: 1px solid var(--line);
  padding: 16px 20px 20px;
  max-height: 62vh;
  overflow-y: auto;
  flex-shrink: 0;
}
.cart__minbar-rail {
  height: 3px;
  border-radius: 999px;
  background: #eee;
  overflow: hidden;
}
.cart__minbar-fill {
  height: 100%;
  border-radius: 999px;
  background: var(--brand);
  transition: width 0.3s;
}
.cart__minbar-text {
  margin: 6px 0 0;
  font-size: 10.5px;
  color: var(--ink-55);
}
.cart__minbar-text--ok {
  color: #1a7f4b;
}
.cart__subtotal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 14px;
}
.cart__subtotal-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-55);
}
.cart__subtotal-value {
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
}
.cart__subtotal-value .tag {
  font-size: 9.5px;
}
.cart__note {
  margin: 8px 0 12px;
  font-size: 11.5px;
  color: var(--ink-55);
}
.cart__form {
  display: grid;
  gap: 8px;
}
.cart__form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.field {
  width: 100%;
  border: 1px solid rgba(18, 18, 18, 0.25);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  color: var(--ink);
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}
.field:focus {
  border-color: var(--ink);
}
.field:disabled {
  background: #f6f6f6;
  color: var(--ink-55);
  cursor: not-allowed;
}
.cart__error {
  margin: 0;
  font-size: 12px;
  color: #b3261e;
}

/* Botones oscuros */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s, background 0.15s;
}
.btn--dark {
  background: var(--ink);
  color: #fff;
  padding: 14px 22px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.btn--dark:hover:not(:disabled) {
  background: #000;
}
.btn--dark:disabled {
  background: #e5e5e5;
  color: #9a9a9a;
  cursor: not-allowed;
}
.btn--block {
  width: 100%;
}

/* ===== Stepper compartido ===== */
.stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 8px;
  overflow: hidden;
}
.stepper button {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--ink);
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.stepper button:hover:not(:disabled) {
  background: rgba(18, 18, 18, 0.06);
}
.stepper button:disabled {
  opacity: 0.35;
  cursor: default;
}
.stepper__num {
  min-width: 30px;
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
}

/* ===== 10) Modal de variantes ===== */
.vfade-enter-active,
.vfade-leave-active {
  transition: opacity 0.2s;
}
.vfade-enter-from,
.vfade-leave-to {
  opacity: 0;
}
.vmodal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 18, 18, 0.5);
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.vmodal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}
.vmodal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  flex-shrink: 0;
}
.vmodal__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.vmodal__body {
  overflow-y: auto;
  padding: 0 16px 16px;
}
.vmodal__loading {
  text-align: center;
  padding: 40px 0;
  font-size: 20px;
  color: var(--ink-55);
}
.vmodal__hint {
  margin: 0 0 12px;
  font-size: 12.5px;
  color: var(--ink-55);
}
.vmodal__item {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(18, 18, 18, 0.12);
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 8px;
  transition: border-color 0.15s;
}
.vmodal__item:hover {
  border-color: var(--ink);
}
.vmodal__item--disabled {
  opacity: 0.45;
}
.vmodal__name {
  font-size: 13px;
  font-weight: 700;
}
.color-dot {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid rgba(18, 18, 18, 0.2);
}
.vmodal__disp {
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.vmodal__disp--ok {
  color: #1a7f4b;
}
.vmodal__disp--out {
  color: #b3261e;
}
.stepper--modal {
  margin-left: auto;
  flex-shrink: 0;
}
.stepper__input {
  width: 34px;
  height: 28px;
  border: none;
  border-left: 1px solid rgba(18, 18, 18, 0.2);
  border-right: 1px solid rgba(18, 18, 18, 0.2);
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
  font-family: inherit;
  color: var(--ink);
  outline: none;
  -moz-appearance: textfield;
}
.stepper__input::-webkit-outer-spin-button,
.stepper__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.vmodal__foot {
  border-top: 1px solid var(--line);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
}
.vmodal__total {
  font-size: 13px;
  color: var(--ink-55);
}
.vmodal__total strong {
  font-size: 15px;
  color: var(--ink);
}
.vmodal__foot .btn {
  padding: 12px 18px;
  flex-shrink: 0;
}

/* ===== Responsive ===== */
@media (min-width: 750px) {
  .pgrid {
    grid-template-columns: repeat(3, 1fr);
    gap: 36px 16px;
  }
}
@media (max-width: 749px) {
  .header__inner {
    height: 58px;
  }
  .header__inner .header__search {
    display: none;
  }
  .header__cart {
    margin-left: auto;
  }
  .header__row2 {
    display: block;
    padding-bottom: 12px;
  }
  .header__row2 .header__search {
    max-width: none;
    margin-left: 0;
  }
  .collection {
    padding-top: 28px;
  }
  .collection__head h1 {
    font-size: 24px;
  }
  .toolbar {
    flex-wrap: wrap;
  }
}
@media (min-width: 1100px) {
  .pgrid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
